import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import AdminSidebar from "../components/AdminSidebar";
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ArrowLeft,
  RefreshCw,
  Flag,
  Eye,
  Ban,
  Trash2,
  MessageSquare,
  Briefcase,
  User,
  Clock,
  Loader2
} from "lucide-react";

const AdminModeration = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [contentTypeFilter, setContentTypeFilter] = useState("");
  const [selectedReport, setSelectedReport] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionNotes, setActionNotes] = useState("");
  const [pendingCount, setPendingCount] = useState(0);
  const [modTab, setModTab] = useState("reviews"); // reviews, content
  const [flaggedReviews, setFlaggedReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({});
  const [loadingFlags, setLoadingFlags] = useState(false);

  useEffect(() => {
    if (user?.role !== "admin") {
      navigate("/");
      return;
    }
    fetchReports();
    fetchFlaggedReviews();
    fetchReviewStats();
  }, [user, statusFilter, contentTypeFilter]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append("status", statusFilter);
      if (contentTypeFilter) params.append("content_type", contentTypeFilter);
      params.append("limit", "100");
      
      const res = await fetch(`${API}/admin/moderation/reports?${params}`, { 
        credentials: "include" 
      });
      if (res.ok) {
        const data = await res.json();
        setReports(data.reports || []);
        setPendingCount(data.pending_count || 0);
      }
    } catch (error) {
      toast.error("Failed to fetch reports");
    } finally {
      setLoading(false);
    }
  };

  const fetchFlaggedReviews = async () => {
    setLoadingFlags(true);
    try {
      const res = await fetch(`${API}/reviews/admin/flagged?status=pending`, { credentials: "include" });
      if (res.ok) setFlaggedReviews(await res.json());
    } catch {}
    finally { setLoadingFlags(false); }
  };

  const fetchReviewStats = async () => {
    try {
      const res = await fetch(`${API}/reviews/admin/stats`, { credentials: "include" });
      if (res.ok) setReviewStats(await res.json());
    } catch {}
  };

  const handleReviewModerate = async (reviewId, action) => {
    try {
      const res = await fetch(`${API}/reviews/admin/${reviewId}/moderate`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action })
      });
      if (res.ok) {
        toast.success(`Review ${action}d`);
        fetchFlaggedReviews();
        fetchReviewStats();
      } else {
        toast.error("Failed to moderate review");
      }
    } catch { toast.error("Action failed"); }
  };

  const handleAction = async (action) => {
    if (!selectedReport) return;
    
    try {
      const res = await fetch(`${API}/admin/moderation/reports/${selectedReport.report_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          action,
          notes: actionNotes
        })
      });
      
      if (res.ok) {
        toast.success(`Report ${action === "dismiss" ? "dismissed" : "resolved"}`);
        setShowActionModal(false);
        setSelectedReport(null);
        setActionNotes("");
        fetchReports();
      } else {
        toast.error("Failed to handle report");
      }
    } catch (error) {
      toast.error("Failed to handle report");
    }
  };

  const getReasonBadge = (reason) => {
    const colors = {
      spam: "bg-yellow-500/20 text-yellow-400",
      inappropriate: "bg-red-500/20 text-red-400",
      misleading: "bg-orange-500/20 text-orange-400",
      harassment: "bg-red-500/20 text-red-400",
      other: "bg-gray-500/20 text-gray-400"
    };
    return colors[reason] || colors.other;
  };

  const getContentIcon = (type) => {
    switch (type) {
      case "job": return <Briefcase className="w-4 h-4 text-purple-400" />;
      case "message": return <MessageSquare className="w-4 h-4 text-cyan-400" />;
      case "profile": return <User className="w-4 h-4 text-green-400" />;
      default: return <Flag className="w-4 h-4 text-gray-400" />;
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "Unknown";
    const date = new Date(timestamp);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian">
      <AdminSidebar />
      
      <main className="lg:ml-64 min-h-screen px-4 sm:px-6 pb-6 pt-20 lg:pt-6">
        <div className="max-w-6xl mx-auto">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-3xl font-bold mb-2">
                CONTENT <span className="text-crimson">MODERATION</span>
              </h1>
              <p className="text-gray-400 flex items-center gap-2">
                Review and moderate reported content
                {pendingCount > 0 && (
                  <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-sm">
                    {pendingCount} Pending
                  </span>
                )}
              </p>
            </div>
            <Button
              onClick={fetchReports}
              variant="outline"
              className="border-[#333] text-white"
              data-testid="refresh-reports-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Review Stats Bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {[
              { label: "Total Reviews", value: reviewStats.total_reviews || 0, color: "text-white" },
              { label: "Under Review", value: reviewStats.under_review || 0, color: "text-amber-400" },
              { label: "Pending Flags", value: reviewStats.pending_flags || 0, color: "text-rose-400" },
              { label: "Verified", value: reviewStats.verified_reviews || 0, color: "text-emerald-400" },
            ].map(s => (
              <div key={s.label} className="bg-obsidian-light border border-[#222] p-3 text-center">
                <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
                <p className="text-xs text-gray-500">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 border-b border-[#222] pb-2">
            <button onClick={() => setModTab("reviews")} className={`px-4 py-2 text-sm font-medium ${modTab === "reviews" ? "text-crimson border-b-2 border-crimson" : "text-gray-500 hover:text-white"}`} data-testid="tab-review-flags">
              Review Flags ({flaggedReviews.length})
            </button>
            <button onClick={() => setModTab("content")} className={`px-4 py-2 text-sm font-medium ${modTab === "content" ? "text-crimson border-b-2 border-crimson" : "text-gray-500 hover:text-white"}`} data-testid="tab-content-reports">
              Content Reports ({pendingCount})
            </button>
          </div>

          {/* Review Flags Tab */}
          {modTab === "reviews" && (
            <div className="bg-obsidian-light border border-[#222] p-6 mb-6">
              <h2 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                <Flag className="w-5 h-5 text-crimson" />
                Flagged Reviews
              </h2>
              {loadingFlags ? (
                <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin text-crimson mx-auto" /></div>
              ) : flaggedReviews.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <p className="text-gray-400">No flagged reviews pending</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {flaggedReviews.map((flag, i) => {
                    const r = flag.review;
                    if (!r) return null;
                    return (
                      <div key={flag.flag_id} className="p-4 bg-obsidian border border-amber-500/30" data-testid={`flagged-${flag.flag_id}`}>
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <span className="text-xs px-2 py-0.5 bg-rose-500/20 text-rose-400 uppercase">{flag.reason?.replace("_", " ")}</span>
                            <span className="text-xs text-gray-500 ml-2">Flagged {new Date(flag.created_at).toLocaleDateString()}</span>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => handleReviewModerate(r.review_id, "approve")} className="bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 text-xs" data-testid={`approve-${r.review_id}`}>
                              Approve
                            </Button>
                            <Button size="sm" onClick={() => handleReviewModerate(r.review_id, "remove")} className="bg-rose-500/20 text-rose-400 hover:bg-rose-500/30 text-xs" data-testid={`remove-${r.review_id}`}>
                              Remove
                            </Button>
                          </div>
                        </div>
                        <div className="mt-2">
                          <p className="font-medium text-sm">{r.title}</p>
                          <p className="text-xs text-gray-500">{r.company_name} · {r.user_name} · Rating: {r.rating_overall}/5</p>
                          <div className="mt-2 text-sm text-gray-400">
                            <p><span className="text-emerald-400 text-xs font-medium">Pros: </span>{r.pros}</p>
                            <p><span className="text-rose-400 text-xs font-medium">Cons: </span>{r.cons}</p>
                          </div>
                          <p className="text-[10px] text-gray-600 mt-2">Review ID: {r.review_id} · Flags: {r.flag_count} · Status: {r.status}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Content Reports Tab */}
          {modTab === "content" && (
          <>
          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-6">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 bg-obsidian-light border border-[#333] text-white focus:border-crimson"
              data-testid="status-filter"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="resolved">Resolved</option>
              <option value="dismissed">Dismissed</option>
            </select>
            <select
              value={contentTypeFilter}
              onChange={(e) => setContentTypeFilter(e.target.value)}
              className="px-4 py-2 bg-obsidian-light border border-[#333] text-white focus:border-crimson"
              data-testid="content-type-filter"
            >
              <option value="">All Content Types</option>
              <option value="job">Job Postings</option>
              <option value="message">Messages</option>
              <option value="profile">Profiles</option>
            </select>
          </div>

          {/* Reports List */}
          <div className="bg-obsidian-light border border-[#222] p-6">
            <h2 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
              <Flag className="w-5 h-5 text-crimson" />
              Reported Content
            </h2>
            
            {reports.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="text-gray-400">No reports matching your filters</p>
              </div>
            ) : (
              <div className="space-y-4">
                {reports.map((report, i) => (
                  <motion.div
                    key={report.report_id}
                    className={`p-4 bg-obsidian border transition-colors ${
                      report.status === "pending" ? "border-yellow-500/30" : "border-[#333]"
                    }`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.02 }}
                    data-testid={`report-${report.report_id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-obsidian-light border border-[#333] flex items-center justify-center">
                        {getContentIcon(report.content_type)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`text-xs px-2 py-0.5 uppercase tracking-wider ${getReasonBadge(report.reason)}`}>
                            {report.reason}
                          </span>
                          <span className={`text-xs px-2 py-0.5 ${
                            report.status === "pending" ? "bg-yellow-500/20 text-yellow-400" :
                            report.status === "resolved" ? "bg-green-500/20 text-green-400" :
                            "bg-gray-500/20 text-gray-400"
                          }`}>
                            {report.status}
                          </span>
                          <span className="text-gray-500 text-xs capitalize">{report.content_type}</span>
                        </div>
                        
                        <p className="text-white text-sm mb-1">
                          <span className="text-gray-400">Content: </span>
                          {report.content_preview || "Content unavailable"}
                        </p>
                        
                        {report.description && (
                          <p className="text-gray-400 text-sm mb-2">
                            <span className="text-gray-500">Details: </span>
                            {report.description}
                          </p>
                        )}
                        
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span>Reported by: {report.reporter_name}</span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatTime(report.created_at)}
                          </span>
                        </div>
                        
                        {report.resolution_notes && (
                          <div className="mt-2 p-2 bg-obsidian-light border border-[#333] text-xs text-gray-400">
                            <span className="text-gray-500">Resolution: </span>
                            {report.action_taken} - {report.resolution_notes}
                          </div>
                        )}
                      </div>
                      
                      {report.status === "pending" && (
                        <div className="flex flex-col gap-2">
                          <Button
                            onClick={() => {
                              setSelectedReport(report);
                              setShowActionModal(true);
                            }}
                            size="sm"
                            className="bg-crimson hover:bg-crimson/90 text-white"
                            data-testid={`review-${report.report_id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Review
                          </Button>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
          </>
          )}
        </div>
      </main>

      {/* Action Modal */}
      {showActionModal && selectedReport && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <motion.div
            className="bg-obsidian-light border border-[#222] p-6 max-w-lg w-full"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <h3 className="font-heading text-xl font-bold mb-4">Review Report</h3>
            
            <div className="mb-4 p-4 bg-obsidian border border-[#333]">
              <p className="text-gray-400 text-sm mb-1">
                <span className="text-gray-500">Type: </span>
                {selectedReport.content_type}
              </p>
              <p className="text-gray-400 text-sm mb-1">
                <span className="text-gray-500">Reason: </span>
                {selectedReport.reason}
              </p>
              <p className="text-white text-sm">
                <span className="text-gray-500">Content: </span>
                {selectedReport.content_preview}
              </p>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm text-gray-400 mb-2">Resolution Notes</label>
              <textarea
                value={actionNotes}
                onChange={(e) => setActionNotes(e.target.value)}
                placeholder="Add notes about your decision..."
                className="w-full px-4 py-2 bg-obsidian border border-[#333] text-white focus:border-crimson focus:outline-none h-24"
                data-testid="action-notes"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3 mb-4">
              <Button
                onClick={() => handleAction("dismiss")}
                variant="outline"
                className="border-gray-500 text-gray-400 hover:bg-gray-500/10"
                data-testid="dismiss-btn"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Dismiss
              </Button>
              <Button
                onClick={() => handleAction("warn_user")}
                variant="outline"
                className="border-yellow-500 text-yellow-400 hover:bg-yellow-500/10"
                data-testid="warn-btn"
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Warn User
              </Button>
              <Button
                onClick={() => handleAction("remove_content")}
                variant="outline"
                className="border-orange-500 text-orange-400 hover:bg-orange-500/10"
                data-testid="remove-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Remove Content
              </Button>
              <Button
                onClick={() => handleAction("ban_user")}
                className="bg-red-500 hover:bg-red-600 text-white"
                data-testid="ban-btn"
              >
                <Ban className="w-4 h-4 mr-2" />
                Ban User
              </Button>
            </div>
            
            <Button
              onClick={() => {
                setShowActionModal(false);
                setSelectedReport(null);
                setActionNotes("");
              }}
              variant="outline"
              className="w-full border-[#333] text-gray-400"
            >
              Cancel
            </Button>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminModeration;
